<template>
  <div class="mx-auto px-4 sm:px-12 xl:max-w-6xl xl:px-0">
    <div class="text-left">
      <h2
        class="text-2xl md:text-5xl font-medium text-gray-800 text-center md:text-start"
        v-motion
        :initial="{ opacity: 0, y: 50 }"
        :visible-once="{
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            delay: 200,
            duration: 800,
            opacity: {
              duration: 800
            }
          }
        }"
      >
        Lo que opinan los clientes
        <br />
        sobre nosotros
      </h2>
    </div>
  </div>

  <div class="relative isolate bg-white pb-16">
    <div aria-hidden="true" class="absolute inset-0 top-60 grid grid-cols-2 -space-x-52 opacity-50">
      <div class="h-60 bg-gradient-to-br from-purple-100 to-purple-400 blur-[106px]"></div>
      <div class="h-40 bg-gradient-to-r from-cyan-400 to-sky-200 blur-[106px]"></div>
    </div>

    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div
        class="relative mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 gap-8 text-sm leading-6 text-gray-900 mt-6 sm:mt-10 sm:grid-cols-2 xl:mx-0 xl:max-w-none xl:grid-flow-col xl:grid-cols-4"
      >
        <figure
          class="rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 sm:col-span-2 xl:col-start-2 xl:row-end-1"
        >
          <blockquote
            class="p-6 sm:font-semibold leading-7 tracking-tight text-gray-900 sm:p-12 sm:text-xl sm:leading-8"
          >
            <p>{{ `“${featuredTestimonial.body}”` }}</p>
          </blockquote>
          <figcaption
            class="flex flex-wrap items-center gap-x-4 gap-y-4 sm:border-t sm:border-gray-900/10 px-6 pb-4 sm:pt-4 sm:flex-nowrap"
          >
            <Image
              class="!h-10 !w-10 flex-none rounded-full bg-gray-50"
              :src="featuredTestimonial.author.imageUrl"
              alt=""
            />
            <div class="flex-auto">
              <div class="font-semibold">{{ featuredTestimonial.author.name }}</div>
              <div class="text-gray-600 text-xs sm:text-sm">
                {{ `${featuredTestimonial.author.handle}` }}
              </div>
            </div>
            <Image
              class="!h-10 !w-auto flex-none hidden sm:block"
              :src="featuredTestimonial.author.logoUrl"
              alt=""
            />
          </figcaption>
        </figure>
        <div
          v-for="(columnGroup, columnGroupIdx) in testimonials"
          :key="columnGroupIdx"
          class="space-y-8 xl:contents xl:space-y-0"
        >
          <div
            v-for="(column, columnIdx) in columnGroup"
            :key="columnIdx"
            :class="[
              (columnGroupIdx === 0 && columnIdx === 0) ||
              (columnGroupIdx === testimonials.length - 1 && columnIdx === columnGroup.length - 1)
                ? 'xl:row-span-2'
                : 'xl:row-start-1',
              'space-y-8'
            ]"
          >
            <figure
              v-for="testimonial in column"
              :key="testimonial.author.handle"
              class="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-900/5"
              v-motion
              :initial="{ opacity: 0, y: 50 }"
              :visible-once="{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: {
                  delay: 200,
                  duration: 800,
                  opacity: {
                    duration: 800
                  }
                }
              }"
            >
              <blockquote class="text-gray-900">
                <p>{{ `“${testimonial.body}”` }}</p>
              </blockquote>
              <figcaption class="mt-6 flex items-center gap-x-4">
                <Image
                  class="!h-10 !w-10 rounded-full bg-gray-50"
                  :src="testimonial.author.imageUrl"
                />
                <div>
                  <div class="font-semibold">{{ testimonial.author.name }}</div>
                  <div class="text-gray-600 text-xs">{{ `${testimonial.author.handle}` }}</div>
                  <div class="text-gray-600 text-xs">{{ `${testimonial.author.company}` }}</div>
                </div>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Image } from '@unpic/vue';

const featuredTestimonial = {
  body:
    '¡Estoy impresionado con la capacidad de Orqui IA para comprender el contexto ' +
    'de cada conversación! Realmente se siente como si estuvieras hablando con un humano, ' +
    '¡pero mucho más rápido y eficiente!',
  author: {
    name: 'Javier López',
    handle: 'Gerente de Ventas en SOFTDEVEL',
    imageUrl: 'images/cards/h1.png',
    logoUrl: 'images/brands/softdevel.png'
  }
};

const testimonials = [
  [
    [
      {
        body:
          'Hemos aumentado nuestra eficiencia en ventas de manera significativa. ' +
          'El agente IA hace recomendaciones personalizadas y puede guiar a los clientes a través' +
          ' del proceso de compra de forma increíble.',
        author: {
          name: 'María González',
          handle: 'Gerente de Marketing',
          company: 'WATCHING',
          imageUrl: 'images/cards/m1.png'
        }
      },
      {
        body: 'La eficienciase convirtió en nuestra aliada gracias a sus respuestas precisas e instantáneas.',
        author: {
          name: 'Alejandro Ruiz',
          handle: 'Director de Operaciones',
          company: 'SOFTDEVEL',
          imageUrl: 'images/cards/h4.png'
        }
      }
      // Más testimonios...
    ],
    [
      {
        body:
          'Hemos logrado transformar por completo nuestra ' +
          'atención al cliente. La capacidad de proporcionar ' +
          'respuestas personalizadas a gran escala es realmente impresionante.',
        author: {
          name: 'Sergio Rodríguez',
          handle: 'Community Manager',
          company: 'GESSMAC',
          imageUrl: 'images/cards/h2.png'
        }
      }
      // Más testimonios...
    ]
  ],
  [
    [
      {
        body:
          'Ahora nuestra bandeja de entrada es universal, lo que ' +
          'significa que vemos todos los mensaje en una sola pantalla, independientemente ' +
          'de la plataforma desde la que nuestros clientes nos escriban.',
        author: {
          name: 'Marta Naula',
          handle: 'Gerente de Logística',
          company: 'Topi Topi',
          imageUrl: 'images/cards/m3.png'
        }
      }
      // Más testimonios...
    ],
    [
      {
        body:
          'Ha simplificado la gestión de nuestras conversaciones en un solo lugar. ' +
          'Ya no perdemos mensajes importantes, y el análisis de resultados que proporciona' +
          ' es invaluable para nuestra empresa',
        author: {
          name: 'Laura Sánchez',
          handle: 'Gerente de Operaciones en ',
          company: 'XEXO MODAS',
          imageUrl: 'images/cards/m2.png'
        }
      },
      {
        body: 'Nuestras operaciones se han simplificado considerablemente gracias a su interfaz tan intuitiva.',
        author: {
          name: 'Roberto Fernández',
          handle: 'Gerente de Tecnología',
          company: 'VPP',
          imageUrl: 'images/cards/h3.png'
        }
      }
      // Más testimonios...
    ]
  ]
];
</script>
